import CobraSpinner from './CobraSpinner';
// Enhanced ProtectedRoute.js with improved error handling and user feedback
import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AuthContext from '../services/AuthContext';
import ResponsiveLayout from './ResponsiveLayout';
import PlatformGate from './PlatformGate';
import {
  Box,
  
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  useToast
} from '@chakra-ui/react';

// Function to detect if the app is accessed from Flutter WebView
function isAccessedFromFlutterWebView() {
  const userAgent = navigator.userAgent;
  const isFlutterWebView = userAgent.includes('Flutter') ||
    userAgent.includes('FlutterWebView') ||
    userAgent.includes('MyFlutterApp'); 
  const isAndroidWebView = userAgent.includes('wv');
  const hasFlutterIdentifier = window.location.search.includes('flutter=true') ||
    document.referrer.includes('flutter') ||
    sessionStorage.getItem('isFlutterApp') === 'true';

  return isFlutterWebView || hasFlutterIdentifier || isAndroidWebView;
}

const isElectronApp = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('electron/') || userAgent.includes('cobradesktopapp');
};

// Loading component
const LoadingScreen = () => (
  <Box
    height="100vh"
    display="flex"
    alignItems="center"
    justifyContent="center"
    bg="gray.50"
  >
    <VStack spacing={4}>
      <CobraSpinner size="xl" color="blue.500" thickness="4px" />
      <Text fontSize="lg" color="gray.600">جاري التحميل...</Text>
    </VStack>
  </Box>
);

// Unauthorized access component
const UnauthorizedAccess = ({ userRole, requiredRoles }) => {
  const toast = useToast();

  React.useEffect(() => {
    toast({
      title: "غير مصرح بالوصول",
      description: "ليس لديك صلاحية للوصول إلى هذه الصفحة",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);

  const handleRedirect = () => {
    const redirectPath = getRoleBasedPath(userRole);
    window.location.href = redirectPath;
  };

  return (
    <Box
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
      p={4}
    >
      <VStack spacing={6} maxW="md" textAlign="center">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>غير مصرح بالوصول!</AlertTitle>
            <AlertDescription>
              ليس لديك صلاحية للوصول إلى هذه الصفحة. سيتم توجيهك إلى لوحة التحكم الخاصة بك.
            </AlertDescription>
          </Box>
        </Alert>

        <Button colorScheme="blue" onClick={handleRedirect}>
          العودة إلى لوحة التحكم
        </Button>

        <Text fontSize="sm" color="gray.500">
          دورك الحالي: {getRoleDisplayName(userRole)}
        </Text>
      </VStack>
    </Box>
  );
};


// Base protected route component with enhanced security
export const ProtectedRoute = ({
  children,
  allowedRoles = [],
  requireAuth = true,
  showUnauthorizedMessage = false
}) => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block web access to protected routes by showing the download gate (students only)
  if (requireAuth && user && user.role === 'student') {
    if (!isAccessedFromFlutterWebView() && !isElectronApp()) {
      return <PlatformGate />;
    }
  }

  if (allowedRoles.length > 0 && user && !allowedRoles.includes(user.role)) {
    if (showUnauthorizedMessage) {
      return <UnauthorizedAccess userRole={user.role} requiredRoles={allowedRoles} />;
    }

    // Redirect to appropriate dashboard based on user role
    const redirectPath = getRoleBasedPath(user.role);
    return <Navigate to={redirectPath} replace />;
  }

  return <ResponsiveLayout>{children}</ResponsiveLayout>;
};

// Admin only routes
export const AdminRoute = ({ children, showUnauthorizedMessage = false }) => (
  <ProtectedRoute
    allowedRoles={['admin']}
    showUnauthorizedMessage={showUnauthorizedMessage}
  >
    {children}
  </ProtectedRoute>
);

// Instructor only routes
export const InstructorRoute = ({ children, showUnauthorizedMessage = false }) => (
  <ProtectedRoute
    allowedRoles={['instructor']}
    showUnauthorizedMessage={showUnauthorizedMessage}
  >
    {children}
  </ProtectedRoute>
);

// Student only routes
export const StudentRoute = ({ children, showUnauthorizedMessage = false }) => (
  <ProtectedRoute
    allowedRoles={['student']}
    showUnauthorizedMessage={showUnauthorizedMessage}
  >
    {children}
  </ProtectedRoute>
);

// Admin and Instructor routes
export const AdminInstructorRoute = ({ children, showUnauthorizedMessage = false }) => (
  <ProtectedRoute
    allowedRoles={['admin', 'instructor']}
    showUnauthorizedMessage={showUnauthorizedMessage}
  >
    {children}
  </ProtectedRoute>
);

// Instructor and Student routes
export const InstructorStudentRoute = ({ children, showUnauthorizedMessage = false }) => (
  <ProtectedRoute
    allowedRoles={['instructor', 'student']}
    showUnauthorizedMessage={showUnauthorizedMessage}
  >
    {children}
  </ProtectedRoute>
);

// All authenticated users
export const AuthenticatedRoute = ({ children, showUnauthorizedMessage = false }) => (
  <ProtectedRoute
    allowedRoles={['admin', 'instructor', 'student']}
    showUnauthorizedMessage={showUnauthorizedMessage}
  >
    {children}
  </ProtectedRoute>
);

// Public routes (no authentication required)
export const PublicRoute = ({ children, redirectIfAuthenticated = false }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return <LoadingScreen />;
  }

  if (redirectIfAuthenticated && user) {
    const redirectPath = getRoleBasedPath(user.role);
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Helper function to get role-based default path
const getRoleBasedPath = (role) => {
  switch (role) {
    case 'admin':
      return '/admin/users';
    case 'instructor':
      return '/instructor/courses';
    case 'student':
      return '/courses';
    default:
      return '/';
  }
};

// Helper function to get role display name in Arabic
const getRoleDisplayName = (role) => {
  switch (role) {
    case 'admin':
      return 'مدير';
    case 'instructor':
      return 'مدرس';
    case 'student':
      return 'طالب';
    default:
      return 'غير محدد';
  }
};

// Role checker hook with additional utilities
export const useRole = () => {
  const { user } = useContext(AuthContext);

  return {
    user,
    role: user?.role,
    isAdmin: user?.role === 'admin',
    isInstructor: user?.role === 'instructor',
    isStudent: user?.role === 'student',
    isAuthenticated: !!user,
    hasRole: (roles) => {
      if (!user) return false;
      if (typeof roles === 'string') return user.role === roles;
      if (Array.isArray(roles)) return roles.includes(user.role);
      return false;
    },
    canAccess: (allowedRoles) => {
      if (!user) return false;
      if (!allowedRoles || allowedRoles.length === 0) return true;
      return allowedRoles.includes(user.role);
    },
    getDefaultPath: () => getRoleBasedPath(user?.role),
    getRoleDisplayName: () => getRoleDisplayName(user?.role)
  };
};

// Higher-order component for conditional rendering based on roles
export const withRoleAccess = (Component, allowedRoles = []) => {
  return (props) => {
    const { canAccess } = useRole();

    if (!canAccess(allowedRoles)) {
      return null;
    }

    return <Component {...props} />;
  };
};

// Component for conditional rendering based on roles
export const RoleGuard = ({ roles, children, fallback = null }) => {
  const { canAccess } = useRole();

  if (!canAccess(roles)) {
    return fallback;
  }

  return children;
};

